import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @fileoverview NeighborhoodGroup — LOCI neighborhood feed section.
 *
 * Tracked-caps `<h3>` with neighborhood name + mono coord (e.g.
 * `UPTOWN · 35.2271° N · 80.8431° W`), followed by a list of 3–5 event rows.
 */
import React, { forwardRef } from 'react';
import { useTheme } from '../../../theme';
import { Coord } from '../Coord';
const COORD_PRECISION = 4;
const formatLatitude = (lat) => `${Math.abs(lat).toFixed(COORD_PRECISION)}° ${lat >= 0 ? 'N' : 'S'}`;
const formatLongitude = (lon) => `${Math.abs(lon).toFixed(COORD_PRECISION)}° ${lon >= 0 ? 'E' : 'W'}`;
/**
 * `<NeighborhoodGroup>` — named neighborhood section composed of an
 * eyebrow/coord header and a vertical stack of `FeedEventRow` children.
 *
 * The header renders an `<h3>` styled with the LOCI Eyebrow tokens beside
 * the `Coord` primitive so tokens stay consistent with the rest of the Home
 * surface. Empty groups render the `emptyState` slot (or a sentence-case
 * default) — keeping the layout intact while the caller waits on data.
 */
export const NeighborhoodGroup = forwardRef(({ name, latitude, longitude, emptyState, children, className, style, 'data-testid': dataTestId = 'neighborhood-group', }, ref) => {
    const theme = useTheme();
    // `React.Children.count` counts `null` entries, which would render an empty
    // list when every child evaluates to null (e.g. a guarded `.map(...)`).
    // Filter to truthy nodes so a null-only children prop falls through to the
    // empty state slot.
    const visibleChildren = React.Children.toArray(children).filter(Boolean);
    const hasChildren = visibleChildren.length > 0;
    const sectionStyle = {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing.md,
    };
    const headerStyle = {
        display: 'flex',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        gap: theme.spacing.sm,
    };
    const listStyle = {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing.xs,
        listStyle: 'none',
        margin: 0,
        padding: 0,
    };
    const emptyStyle = {
        color: theme.colors.textMuted,
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.fontSize.small,
        padding: `${theme.spacing.md} 0`,
    };
    // Mirrors the Eyebrow atom's tracked-caps treatment so the neighborhood
    // name keeps its visual identity while exposing a real <h3> heading for
    // assistive tech (WEB-149).
    const headingStyle = {
        margin: 0,
        fontFamily: theme.typography.fontFamilyDisplay ?? theme.typography.fontFamily,
        fontWeight: theme.typography.fontWeight.semibold,
        fontSize: theme.typography.fontSize.caption,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: theme.colors.accent,
    };
    const coord = `${formatLatitude(latitude)} · ${formatLongitude(longitude)}`;
    return (_jsxs("section", { ref: ref, className: className, style: { ...sectionStyle, ...style }, "aria-label": `${name} events`, "data-testid": dataTestId, "data-neighborhood": name, children: [_jsxs("header", { style: headerStyle, "data-testid": "neighborhood-group-header", children: [_jsx("h3", { style: headingStyle, children: name }), _jsx(Coord, { size: "xs", "aria-hidden": "true", children: coord })] }), hasChildren ? (_jsx("ul", { style: listStyle, "data-testid": "neighborhood-group-list", children: visibleChildren.map((child, index) => {
                    const element = child;
                    const childKey = element?.key ?? index;
                    return (_jsx("li", { role: "none", style: { listStyle: 'none' }, children: child }, childKey));
                }) })) : (_jsx("div", { style: emptyStyle, "data-testid": "neighborhood-group-empty", children: emptyState ?? `No events in ${name} right now.` }))] }));
});
NeighborhoodGroup.displayName = 'NeighborhoodGroup';
